import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import {
  FrappeGanttView,
  type GanttViewMode,
} from "@/components/FrappeGanttView";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import { Modal } from "@/components/Modal";
import { DependencySection } from "@/components/DependencySection";
import { toast } from "@/components/Toast";
import { computeCriticalPath } from "@/lib/criticalPath";
import {
  levelResources,
  type LevelTask,
  type LevelProposal,
} from "@/lib/resourceLevelling";
import { parseDateLocal } from "@/lib/format";

/**
 * Export the Gantt to a shareable PDF via the browser's print dialog
 * ("Save as PDF"). A body class scopes a print stylesheet (screens.css) that
 * hides the app chrome and lets the timeline span pages in landscape.
 */
function exportGanttPdf() {
  const body = document.body;
  body.classList.add("printing-timeline");
  const cleanup = () => {
    body.classList.remove("printing-timeline");
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);
  // Fallback in case afterprint never fires (some browsers/print-to-file).
  window.setTimeout(cleanup, 1000);
  window.print();
}

/**
 * Gantt view — the Timeline plus a computed critical-path highlight and
 * percent-complete fills on each bar.
 */
function GanttView() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [levelOpen, setLevelOpen] = useState(false);
  const [showCritical, setShowCritical] = useState(true);
  const [viewMode, setViewMode] = useState<GanttViewMode>("Week");
  const [depMode, setDepMode] = useState(false);
  const [depTaskId, setDepTaskId] = useState<string | null>(null);
  const [autoSchedule, setAutoSchedule] = useState(false);

  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });
  const teamQ = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const msQ = useQuery({
    queryKey: queryKeys.milestonesFor(projectId),
    queryFn: () => api.listMilestones(projectId),
  });
  const edgesQ = useQuery({
    queryKey: queryKeys.projectDeps(projectId),
    queryFn: () => api.projectDependencies(projectId),
  });

  const tasks = tasksQ.data?.tasks ?? [];
  const edges = edgesQ.data?.edges ?? [];

  const users = useMemo(() => {
    const map = new Map<string, { name: string; image: string | null }>();
    for (const m of teamQ.data?.members ?? []) {
      map.set(m.id, {
        name: m.name ?? m.email ?? m.id.slice(0, 8),
        image: m.image ?? null,
      });
    }
    return map;
  }, [teamQ.data]);

  const criticalIds = useMemo(
    () => computeCriticalPath(tasks, edges),
    [tasks, edges],
  );

  return (
    <div className="proj-shell-content">
      <div className="board-bar" style={{ marginBottom: 12 }}>
        <SegmentedToggle
          options={[
            { value: "board", label: "Board", icon: "board" },
            { value: "list", label: "List", icon: "clip" },
            { value: "table", label: "Table", icon: "grid" },
            { value: "calendar", label: "Calendar", icon: "cal" },
            { value: "timeline", label: "Timeline", icon: "arrow" },
            { value: "gantt", label: "Gantt", icon: "chev" },
          ]}
          value="gantt"
          onChange={(v) =>
            navigate({
              to:
                v === "list"
                  ? "/projects/$projectId/list"
                  : v === "table"
                    ? "/projects/$projectId/table"
                    : v === "calendar"
                      ? "/projects/$projectId/calendar"
                      : v === "timeline"
                        ? "/projects/$projectId/timeline"
                        : v === "gantt"
                          ? "/projects/$projectId/gantt"
                          : "/projects/$projectId",
              params: { projectId },
            })
          }
        />
        {criticalIds.size > 0 && (
          <button
            type="button"
            className={`chip-btn${showCritical ? " on" : ""}`}
            onClick={() => setShowCritical((c) => !c)}
            title="Highlight the critical path"
          >
            <span style={{ color: "#dc2626", fontWeight: 700 }}>◼</span>{" "}
            Critical path ({criticalIds.size})
          </button>
        )}
        <button
          type="button"
          className={`chip-btn${depMode ? " on" : ""}`}
          onClick={() => setDepMode((d) => !d)}
          title="Add or remove dependency links by picking a task"
        >
          ⛓ Dependencies
        </button>
        <button
          type="button"
          className={`chip-btn${autoSchedule ? " on" : ""}`}
          onClick={() => setAutoSchedule((a) => !a)}
          title="When on, dragging a task pushes its dependents forward to keep the schedule valid"
        >
          ⏩ Auto-schedule
        </button>
        <Button
          variant="secondary"
          onClick={() => setLevelOpen(true)}
          style={{ marginLeft: "auto" }}
        >
          Level resources
        </Button>
        <Button variant="secondary" onClick={exportGanttPdf}>
          Export PDF
        </Button>
      </div>

      {levelOpen && (
        <LevelResourcesModal
          projectId={projectId}
          tasks={tasks}
          users={users}
          onClose={() => setLevelOpen(false)}
        />
      )}

      {depMode && (
        <DependencyEditor
          projectId={projectId}
          tasks={tasks}
          depTaskId={depTaskId}
          onPick={setDepTaskId}
          onChanged={() =>
            qc.invalidateQueries({
              queryKey: queryKeys.projectDeps(projectId),
            })
          }
          onClose={() => {
            setDepMode(false);
            setDepTaskId(null);
          }}
        />
      )}

      {tasksQ.isLoading ? (
        <Skeleton count={6} height={40} />
      ) : (
        <FrappeGanttView
          projectId={projectId}
          tasks={tasks}
          edges={edges}
          milestones={msQ.data?.milestones ?? []}
          users={users}
          criticalIds={criticalIds}
          showCritical={showCritical}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          depMode={depMode}
          onSelectTask={setDepTaskId}
          autoSchedule={autoSchedule}
        />
      )}
    </div>
  );
}

/**
 * Inline dependency editor for the Gantt page (task_awwhqj). Pick a task — from
 * the dropdown or by clicking its bar while dep-mode is on — then add/remove
 * its blockers with the shared DependencySection. Each change refetches the
 * project's edge set so the chart's arrows redraw live.
 */
function DependencyEditor({
  projectId,
  tasks,
  depTaskId,
  onPick,
  onChanged,
  onClose,
}: {
  projectId: string;
  tasks: Array<{ id: string; title: string; parentTaskId: string | null }>;
  depTaskId: string | null;
  onPick: (taskId: string) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const topTasks = tasks.filter((t) => t.parentTaskId == null);
  return (
    <div className="dep-editor">
      <div className="dep-editor-head">
        <label className="dep-editor-pick">
          <span className="t-sm t-muted">Edit dependencies for</span>
          <select
            className="select"
            value={depTaskId ?? ""}
            onChange={(e) => e.target.value && onPick(e.target.value)}
            aria-label="Pick a task to edit dependencies"
          >
            <option value="">Select a task (or click a bar)…</option>
            {topTasks.map((t) => (
              <option key={t.id} value={t.id}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="icon-btn"
          aria-label="Close dependency editor"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      {depTaskId ? (
        <DependencySection
          key={depTaskId}
          taskId={depTaskId}
          projectId={projectId}
          onChanged={onChanged}
        />
      ) : (
        <p className="t-sm t-muted" style={{ margin: "8px 0 0" }}>
          Pick a task above, or click any bar on the chart, to manage what
          it&rsquo;s blocked by.
        </p>
      )}
    </div>
  );
}

/**
 * Level Resources — compute a serial schedule per assignee from capacity +
 * estimates, preview the proposed start-date shifts, lock any task you want to
 * pin, then apply. Locked tasks are never moved and others schedule around them.
 */
function LevelResourcesModal({
  projectId,
  tasks,
  users,
  onClose,
}: {
  projectId: string;
  tasks: Array<{
    id: string;
    title: string;
    assigneeIds: string[];
    estimate: number | null;
    startDate: string | null;
    dueDate: string | null;
    priority: number;
    status: string;
    startLocked?: boolean | number;
  }>;
  users: Map<string, { name: string; image: string | null }>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const workloadQ = useQuery({
    queryKey: ["team", "workload"],
    queryFn: api.teamWorkload,
  });
  const capByUser = useMemo(() => {
    const m = new Map<string, number | null>();
    for (const w of workloadQ.data?.members ?? []) m.set(w.userId, w.capacity);
    return m;
  }, [workloadQ.data]);

  // Today (browser-local) as the scheduling anchor.
  const anchor = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  }, []);

  const levelTasks: LevelTask[] = tasks.map((t) => ({
    id: t.id,
    title: t.title,
    assigneeIds: t.assigneeIds,
    estimate: t.estimate,
    startDate: t.startDate,
    dueDate: t.dueDate,
    priority: t.priority,
    status: t.status,
    startLocked: t.startLocked,
  }));
  const proposals = useMemo(
    () => levelResources(levelTasks, capByUser, { anchor }),
    [levelTasks, capByUser, anchor],
  );

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.tasksFor(projectId) });
  const lockMut = useMutation({
    mutationFn: ({ id, locked }: { id: string; locked: boolean }) =>
      api.patchTask(id, { startLocked: locked }),
    onSuccess: invalidate,
  });
  const apply = useMutation({
    mutationFn: async (ps: LevelProposal[]) => {
      // Sequential so we don't hammer D1 with a burst of writes.
      for (const p of ps) {
        await api.patchTask(p.id, { startDate: p.toStart, dueDate: p.toDue });
      }
    },
    onSuccess: () => {
      invalidate();
      toast.success("Resource levelling applied.");
      onClose();
    },
    onError: () => toast.error("Couldn't apply levelling. Try again."),
  });

  const lockedById = new Map(
    tasks.map((t) => [t.id, Boolean(t.startLocked)] as const),
  );
  const fmt = (s: string | null) => {
    const d = s ? parseDateLocal(s) : null;
    return d ? d.toLocaleDateString() : "—";
  };

  return (
    <Modal open onClose={onClose} title="Level resources" width={620}>
      <p className="t-sm t-muted" style={{ marginTop: 0 }}>
        Schedules each assignee&rsquo;s tasks back-to-back from today using
        their weekly capacity and each task&rsquo;s estimate. Lock a task to pin
        its start date — locked tasks are never moved and others schedule around
        them.
      </p>
      {proposals.length === 0 ? (
        <p className="t-sm">
          Nothing to move — every assigned, estimated task is already levelled
          (or there are no schedulable tasks).
        </p>
      ) : (
        <ul className="level-list">
          {proposals.map((p) => (
            <li key={p.id} className="level-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="level-title">{p.title}</div>
                <div className="t-sm t-muted">
                  {users.get(p.assigneeId)?.name ?? "Unassigned"} ·{" "}
                  {p.durationDays}d
                </div>
              </div>
              <div className="level-dates t-sm">
                <span className="t-muted">{fmt(p.fromStart)}</span> →{" "}
                <strong>{fmt(p.toStart)}</strong>
              </div>
              <label className="level-lock t-sm" title="Lock this start date">
                <input
                  type="checkbox"
                  checked={lockedById.get(p.id) ?? false}
                  disabled={lockMut.isPending}
                  onChange={(e) =>
                    lockMut.mutate({ id: p.id, locked: e.target.checked })
                  }
                />
                Lock
              </label>
            </li>
          ))}
        </ul>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          marginTop: 16,
        }}
      >
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          disabled={proposals.length === 0 || apply.isPending}
          onClick={() => apply.mutate(proposals)}
        >
          {apply.isPending
            ? "Applying…"
            : `Apply ${proposals.length} change${proposals.length === 1 ? "" : "s"}`}
        </Button>
      </div>
    </Modal>
  );
}

export const Route = createFileRoute("/projects/$projectId/gantt")({
  component: GanttView,
});
